import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { leaveApi } from '../lib/api';
import { formatLeave, resolveUnit } from '../lib/leaveUnits';

/**
 * "Next leave year" — a small section of its own under the holiday balance, shown ONLY
 * when something is booked in next leave year.
 *
 * ⚠️ DESIGN-leave-years.md decision H. Rob picked option B from the mock-up on 29/09/2026,
 * after the first version (one line: "3 days booked, 27 days left") "could do with more
 * information". Leave booked into next leave year comes out of next year's balance, so
 * today's balance rightly doesn't move — this is where the person sees where it went.
 *
 * Everything comes from GET /leave/allowances/next-year, which uses the same stored split
 * the ledger charged, so a booking across the start of the year shows exactly the days
 * that came off next year. "Of N" is what next year leaves them to choose freely (left +
 * booked), so the numbers on the card add up by themselves.
 *
 * `reloadKey`: anything that changes when the page reloads its data, so a new booking or
 * a cancellation shows here too.
 */
const day = (iso) => format(parseISO(iso), 'EEE d MMM');
const dmy = (iso) => format(parseISO(iso), 'dd/MM/yyyy');

export default function NextLeaveYear({ reloadKey, className }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    leaveApi.getMyNextLeaveYear()
      .then((r) => { if (live) setData(r.data || null); })
      // Nothing is better than something wrong: a failed load simply shows nothing.
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [reloadKey]);

  const a = data && data.allowance;
  if (!a) return null;
  const bookedDays = (a.used || 0) + (a.pending || 0);
  if (!(bookedDays > 0)) return null;

  const has = (v) => v !== null && v !== undefined;
  const bookedMin = has(a.used_minutes) && has(a.pending_minutes) ? a.used_minutes + a.pending_minutes : null;
  const ofDays = (a.remaining || 0) + bookedDays;
  const ofMin = has(a.remaining_minutes) && has(bookedMin) ? a.remaining_minutes + bookedMin : null;
  const unit = resolveUnit(a.display_unit, a.remaining_minutes, bookedMin, ofMin);
  const pct = ofDays > 0 ? Math.min(100, Math.max(0, (bookedDays / ofDays) * 100)) : 0;
  const carry = data.carry_over || {};

  return (
    <div className={className} data-testid="next-leave-year">
      <div className="border-t border-border/60 my-3" />
      {/* Wraps as whole phrases: in My Leave's narrow sidebar the date drops onto its own
          line rather than splitting "starts / 01/04/2027" (seen on Rob's PC, 29/09/2026). */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="text-sm font-semibold whitespace-nowrap">Next leave year · {data.label}</span>
        <span className="text-xs text-muted-foreground whitespace-nowrap">starts {dmy(data.starts)}</span>
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span className="text-2xl font-heading font-bold">
          {formatLeave(a.remaining, { unit, minutes: a.remaining_minutes, withUnit: false })}
        </span>
        <span className="text-sm text-muted-foreground">
          of {formatLeave(ofDays, { unit, minutes: ofMin })} left
        </span>
      </div>
      <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-2 space-y-0.5 text-xs">
        {(data.bookings || []).map((b) => (
          <li key={`${b.id}-${b.from}`} className="flex items-baseline justify-between gap-3">
            <span>{b.from === b.to ? day(b.from) : `${day(b.from)} – ${day(b.to)}`}</span>
            <span className="text-muted-foreground text-right">
              {formatLeave(b.days, { unit, minutes: b.minutes })}
              {(b.booking_from || b.booking_to) && (
                <> · part of your {dmy(b.booking_from || b.from)} – {dmy(b.booking_to || b.to)} booking</>
              )}
              {b.waiting && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 text-[10px] font-medium">waiting</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {carry.enabled && carry.added_on_start && (
        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
          Unused days from this year{carry.max_days ? ` (up to ${formatLeave(carry.max_days)})` : ''} are
          added on {dmy(data.starts)}, when the new year starts.
        </p>
      )}
    </div>
  );
}
